<template>
  <section class="glad-discussion">
    <header class="glad-discussion__header">
      <h2 class="glad-discussion__heading">Discussion</h2>
      <span v-if="!loading" class="glad-discussion__count">
        {{ comments.length }} {{ comments.length === 1 ? 'comment' : 'comments' }}
      </span>
    </header>

    <!-- Composer -->
    <form class="glad-discussion__composer" @submit.prevent="submit">
      <textarea
        v-model="draft"
        class="glad-discussion__input"
        :placeholder="placeholder"
        rows="3"
        maxlength="5000"
        :disabled="posting"
        @keydown.meta.enter.prevent="submit"
        @keydown.ctrl.enter.prevent="submit">
      </textarea>
      <div class="glad-discussion__composer-row">
        <span class="glad-discussion__hint">
          ⌘ / Ctrl + Enter to post
        </span>
        <button
          type="submit"
          class="glad-btn glad-btn--sm"
          :disabled="posting || !canSubmit">
          {{ posting ? 'Posting…' : 'Post comment' }}
        </button>
      </div>
      <p v-if="error" class="glad-discussion__error" role="alert">{{ error }}</p>
    </form>

    <!-- Loading -->
    <div v-if="loading" class="glad-discussion__loading">
      <span class="glad-spinner glad-spinner--sm"></span>
      <span>Loading discussion…</span>
    </div>

    <!-- Empty state -->
    <p v-else-if="!comments.length" class="glad-discussion__empty">
      No comments yet — be the first to share a reflection on this module.
    </p>

    <!-- Thread -->
    <ul v-else class="glad-discussion__list">
      <li
        v-for="c in comments"
        :key="c.id"
        class="glad-thread">
        <!-- Top-level comment -->
        <div
          class="glad-comment"
          :class="{ 'glad-comment--deleted': c.is_deleted }">
          <div class="glad-comment__avatar" aria-hidden="true">
            {{ initials(c.author && c.author.name) }}
          </div>
          <div class="glad-comment__main">
            <div class="glad-comment__head">
              <span class="glad-comment__name">{{ c.author && c.author.name || 'Unknown' }}</span>
              <span v-if="c.author && c.author.role_label" class="glad-comment__role">
                {{ c.author.role_label }}
              </span>
              <span class="glad-comment__time">{{ formatDate(c.created_at) }}</span>
              <button
                v-if="c.is_mine && !c.is_deleted"
                type="button"
                class="glad-comment__delete"
                :disabled="deletingId === c.id"
                @click="confirmDelete(c.id)">
                {{ deletingId === c.id ? 'Deleting…' : 'Delete' }}
              </button>
            </div>
            <p v-if="c.is_deleted" class="glad-comment__body glad-comment__body--deleted">
              [comment removed by author]
            </p>
            <p v-else class="glad-comment__body">{{ c.body }}</p>
            <div v-if="!c.is_deleted" class="glad-comment__actions">
              <button
                type="button"
                class="glad-comment__reply-toggle"
                @click="toggleReply(c.id)">
                {{ replyingTo === c.id ? 'Cancel' : 'Reply' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Replies + reply composer -->
        <div v-if="(c.replies && c.replies.length) || replyingTo === c.id" class="glad-replies">
          <div
            v-for="r in c.replies"
            :key="r.id"
            class="glad-comment glad-comment--reply"
            :class="{ 'glad-comment--deleted': r.is_deleted }">
            <div class="glad-comment__avatar glad-comment__avatar--sm" aria-hidden="true">
              {{ initials(r.author && r.author.name) }}
            </div>
            <div class="glad-comment__main">
              <div class="glad-comment__head">
                <span class="glad-comment__name">{{ r.author && r.author.name || 'Unknown' }}</span>
                <span v-if="r.author && r.author.role_label" class="glad-comment__role">
                  {{ r.author.role_label }}
                </span>
                <span class="glad-comment__time">{{ formatDate(r.created_at) }}</span>
                <button
                  v-if="r.is_mine && !r.is_deleted"
                  type="button"
                  class="glad-comment__delete"
                  :disabled="deletingId === r.id"
                  @click="confirmDelete(r.id)">
                  {{ deletingId === r.id ? 'Deleting…' : 'Delete' }}
                </button>
              </div>
              <p v-if="r.is_deleted" class="glad-comment__body glad-comment__body--deleted">
                [comment removed by author]
              </p>
              <p v-else class="glad-comment__body">{{ r.body }}</p>
            </div>
          </div>

          <!-- Reply composer -->
          <form
            v-if="replyingTo === c.id"
            class="glad-reply-composer"
            @submit.prevent="submitReply(c)">
            <textarea
              v-model="replyDraft"
              class="glad-discussion__input"
              placeholder="Write a reply…"
              rows="2"
              maxlength="5000"
              :disabled="replyPosting"
              @keydown.meta.enter.prevent="submitReply(c)"
              @keydown.ctrl.enter.prevent="submitReply(c)">
            </textarea>
            <div class="glad-discussion__composer-row">
              <span class="glad-discussion__hint">Replying to {{ c.author && c.author.name || 'this comment' }}</span>
              <button
                type="submit"
                class="glad-btn glad-btn--sm"
                :disabled="replyPosting || !replyDraft.trim()">
                {{ replyPosting ? 'Posting…' : 'Post reply' }}
              </button>
            </div>
            <p v-if="replyError" class="glad-discussion__error" role="alert">{{ replyError }}</p>
          </form>
        </div>
      </li>
    </ul>
  </section>
</template>

<script>
export default {
  name: 'DiscussionThread',
  props: {
    pathwayId: { type: [Number, String], required: true },
    moduleId:  { type: [Number, String], required: true },
  },
  data() {
    return {
      loading: true,
      posting: false,
      deletingId: null,
      draft: '',
      error: '',
      comments: [],
      // Reply state — only one reply composer is open at a time.
      replyingTo: null,
      replyDraft: '',
      replyPosting: false,
      replyError: '',
    };
  },
  computed: {
    canSubmit() {
      return this.draft.trim().length > 0;
    },
    placeholder() {
      return this.comments.length === 0
        ? 'Share a reflection, question, or strategy you tried…'
        : 'Add to the discussion…';
    },
  },
  watch: {
    moduleId() {
      // Re-fetch when ModuleViewer navigates between modules without
      // remounting this component.
      this.comments = [];
      this.fetchComments();
    },
  },
  created() {
    this.fetchComments();
  },
  methods: {
    apiBase() {
      return `glad/pathways/${this.pathwayId}/modules/${this.moduleId}/discussions`;
    },
    fetchComments() {
      this.loading = true;
      this.$http.get(this.apiBase())
        .then(resp => {
          const list = (resp && resp.data && Array.isArray(resp.data.data)) ? resp.data.data : [];
          // Defensive: guarantee a replies array on every thread so the
          // template can iterate it even if an older API omits the field.
          this.comments = list.map(c => ({ ...c, replies: Array.isArray(c.replies) ? c.replies : [] }));
        })
        .catch(() => { this.comments = []; })
        .finally(() => { this.loading = false; });
    },
    submit() {
      if (!this.canSubmit || this.posting) return;
      this.posting = true;
      this.error = '';
      this.$http.post(this.apiBase(), { body: this.draft.trim() })
        .then(resp => {
          // Splice the new comment in at the top (matches server sort).
          const created = { ...resp.data, replies: Array.isArray(resp.data.replies) ? resp.data.replies : [] };
          this.comments.unshift(created);
          this.draft = '';
        })
        .catch(err => {
          let msg = 'We could not post your comment. Please try again.';
          if (err && err.response && err.response.data) {
            if (err.response.data.message) msg = err.response.data.message;
            if (err.response.data.errors) {
              const list = Object.values(err.response.data.errors).flat();
              if (list.length) msg = list.join(' ');
            }
          }
          this.error = msg;
        })
        .finally(() => { this.posting = false; });
    },
    toggleReply(id) {
      this.replyError = '';
      if (this.replyingTo === id) {
        this.replyingTo = null;
        this.replyDraft = '';
      } else {
        this.replyingTo = id;
        this.replyDraft = '';
      }
    },
    submitReply(parent) {
      const body = this.replyDraft.trim();
      if (!body || this.replyPosting) return;
      this.replyPosting = true;
      this.replyError = '';
      this.$http.post(this.apiBase(), { body, parent_id: parent.id })
        .then(resp => {
          // Replies render oldest-first, so append to the end of the thread.
          if (!Array.isArray(parent.replies)) {
            this.$set(parent, 'replies', []);
          }
          parent.replies.push(resp.data);
          this.replyDraft = '';
          this.replyingTo = null;
        })
        .catch(err => {
          let msg = 'We could not post your reply. Please try again.';
          if (err && err.response && err.response.data) {
            if (err.response.data.message) msg = err.response.data.message;
            if (err.response.data.errors) {
              const list = Object.values(err.response.data.errors).flat();
              if (list.length) msg = list.join(' ');
            }
          }
          this.replyError = msg;
        })
        .finally(() => { this.replyPosting = false; });
    },
    // Find a comment by id whether it's a top-level thread or a reply.
    findComment(id) {
      for (const c of this.comments) {
        if (c.id === id) return c;
        if (Array.isArray(c.replies)) {
          const r = c.replies.find(x => x.id === id);
          if (r) return r;
        }
      }
      return null;
    },
    confirmDelete(id) {
      if (!window.confirm('Delete this comment? Other learners will see it as removed.')) return;
      this.deletingId = id;
      this.$http.delete(`glad/discussions/${id}`)
        .then(() => {
          const c = this.findComment(id);
          if (c) {
            this.$set(c, 'is_deleted', true);
            this.$set(c, 'body', '');
          }
        })
        .catch(() => {
          this.error = 'Could not delete the comment. Please try again.';
        })
        .finally(() => { this.deletingId = null; });
    },
    initials(name) {
      if (!name) return '·';
      const parts = String(name).trim().split(/\s+/);
      const first = parts[0] ? parts[0][0] : '';
      const last  = parts.length > 1 ? parts[parts.length - 1][0] : '';
      return (first + last).toUpperCase() || '·';
    },
    formatDate(d) {
      if (!d) return '';
      // Laravel returns "YYYY-MM-DD HH:MM:SS" without a timezone marker.
      // Default Date parsing treats that as local time, which makes a
      // just-posted comment look like it's hours old. Force UTC parsing
      // by appending the Z suffix when the input lacks a timezone.
      const normalized = /[zZ]|[+\-]\d{2}:?\d{2}$/.test(d)
        ? d
        : d.replace(' ', 'T') + 'Z';
      const parsed = new Date(normalized);
      if (isNaN(parsed.getTime())) return d;
      const now = new Date();
      const diffMs = now.getTime() - parsed.getTime();
      const diffMin = Math.round(diffMs / 60000);
      if (diffMin < 1) return 'just now';
      if (diffMin < 60) return `${diffMin}m ago`;
      const diffHr = Math.round(diffMin / 60);
      if (diffHr < 24) return `${diffHr}h ago`;
      const diffDay = Math.round(diffHr / 24);
      if (diffDay < 7) return `${diffDay}d ago`;
      try {
        return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
      } catch (e) {
        return d;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/sass/glad-tokens.scss';

.glad-discussion {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--glad-line);
  font-family: var(--glad-font-sans);
}

.glad-discussion__header {
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  margin-bottom: 0.85rem;
}
.glad-discussion__heading {
  font-family: var(--glad-font-serif);
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--glad-ink);
  margin: 0;
}
.glad-discussion__count {
  font-size: 0.78rem;
  color: var(--glad-text-muted);
}

/* Composer ----------------------------------------------------------------- */
.glad-discussion__composer {
  background: var(--glad-paper);
  border: 1px solid var(--glad-line);
  border-radius: var(--glad-radius-lg);
  padding: 0.85rem;
  margin-bottom: 1.25rem;
}

.glad-discussion__input {
  display: block;
  width: 100%;
  border: 0;
  outline: none;
  background: transparent;
  resize: vertical;
  min-height: 60px;
  font-family: var(--glad-font-sans);
  font-size: 0.9rem;
  line-height: 1.5;
  color: var(--glad-ink);

  &::placeholder { color: var(--glad-text-muted); }
  &:disabled { opacity: 0.5; }
}

.glad-discussion__composer-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.85rem;
  margin-top: 0.6rem;
  padding-top: 0.6rem;
  border-top: 1px dashed var(--glad-line);
}
.glad-discussion__hint {
  font-size: 0.72rem;
  color: var(--glad-text-muted);
}
.glad-discussion__error {
  margin: 0.6rem 0 0;
  font-size: 0.78rem;
  color: var(--glad-danger);
}

/* List + comment ----------------------------------------------------------- */
.glad-discussion__loading {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  color: var(--glad-text-soft);
  font-size: 0.85rem;
  padding: 1rem 0;
}
.glad-discussion__empty {
  font-size: 0.9rem;
  color: var(--glad-text-soft);
  text-align: center;
  padding: 1.5rem 0;
  margin: 0;
}

.glad-discussion__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.glad-comment {
  display: flex;
  gap: 0.85rem;
  padding: 0.85rem;
  background: var(--glad-surface);
  border: 1px solid var(--glad-line);
  border-radius: var(--glad-radius-lg);
}
.glad-comment--deleted { opacity: 0.6; }

.glad-comment__avatar {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--glad-paper-2);
  color: var(--glad-ink);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.03em;
}

.glad-comment__main { flex: 1; min-width: 0; }

.glad-comment__head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 0.25rem;
}
.glad-comment__name {
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--glad-ink);
}
.glad-comment__role {
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  background: var(--glad-paper-2);
  color: var(--glad-text-soft);
}
.glad-comment__time {
  font-size: 0.74rem;
  color: var(--glad-text-muted);
}
.glad-comment__delete {
  margin-left: auto;
  background: transparent;
  border: 0;
  color: var(--glad-text-muted);
  font-size: 0.74rem;
  cursor: pointer;
  font-family: var(--glad-font-sans);
  &:hover { color: var(--glad-danger); }
  &:disabled { cursor: not-allowed; opacity: 0.6; }
}

.glad-comment__body {
  font-size: 0.9rem;
  line-height: 1.55;
  color: var(--glad-ink);
  white-space: pre-wrap;
  word-wrap: break-word;
  margin: 0;
}
.glad-comment__body--deleted {
  color: var(--glad-text-muted);
  font-style: italic;
}

/* Replies + reply composer ------------------------------------------------- */
.glad-thread {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}
.glad-comment__actions {
  margin-top: 0.4rem;
}
.glad-comment__reply-toggle {
  background: transparent;
  border: 0;
  padding: 0;
  color: var(--glad-text-soft);
  font-family: var(--glad-font-sans);
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  &:hover { color: var(--glad-ink); }
}
.glad-replies {
  margin-left: 2.6rem;
  padding-left: 0.9rem;
  border-left: 2px solid var(--glad-line);
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}
.glad-comment--reply {
  background: var(--glad-paper);
}
.glad-comment__avatar--sm {
  width: 28px;
  height: 28px;
  font-size: 0.66rem;
}
.glad-reply-composer {
  background: var(--glad-paper);
  border: 1px solid var(--glad-line);
  border-radius: var(--glad-radius-lg);
  padding: 0.7rem 0.8rem;
}

/* Reuse button + spinner styles from the host page where possible.
   These local copies keep the component self-sufficient if dropped
   into another page without the host's tokens already loaded. */
.glad-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.4rem 0.95rem;
  background: var(--glad-ink);
  color: #fff;
  border: 0;
  border-radius: var(--glad-radius);
  font-family: var(--glad-font-sans);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: opacity 0.15s ease;
  &:hover { opacity: 0.85; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
  &--sm { padding: 0.38rem 0.85rem; font-size: 0.8rem; }
}

.glad-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid var(--glad-line);
  border-top-color: var(--glad-ink);
  border-radius: 50%;
  animation: glad-spin 0.7s linear infinite;
  display: inline-block;
}
@keyframes glad-spin { to { transform: rotate(360deg); } }
</style>
